/* ============================================================================
 * ตัวช่วยเรื่องตำแหน่งที่ตั้ง — ใช้ร่วมกันระหว่าง
 *   - จัดการทรัพย์ › เพิ่ม/แก้ไขทรัพย์ (page/admin/asset_create.js) ขั้นที่ 1
 *   - จัดการทรัพย์ › โครงการ         (page/admin/projects.js)
 *
 * สองหน้านี้กรอก "ตำแหน่งที่ตั้ง" ชุดเดียวกันเป๊ะ (จังหวัด › เขต/อำเภอ › แขวง/ตำบล ›
 * ซอย › รหัสไปรษณีย์ › พิกัด › จุดสนใจใกล้เคียง) จึงย้ายค่าคงที่ของแผนที่และตัวจับคู่
 * ชื่อทำเลมาไว้ที่เดียว ไม่งั้นแก้ที่หนึ่งแล้วอีกหน้าจะเพี้ยนตาม
 * ========================================================================== */

import PROVINCE from "../assets/files/th_province.json";
import TRANSIT_STATION from "../assets/files/th_transit_station.json";

/* ------------------------------------------------------------------ แผนที่ */
export const MAP_KEY = process.env.REACT_APP_API_MAP_KEY;
/* ต้องมี "places" เพื่อใช้ AutocompleteSuggestion (ช่องค้นหาแบบมีรายการแนะนำ)
   อาร์เรย์นี้ต้องเป็นค่าคงที่ระดับโมดูล ห้ามสร้างใหม่ทุก render ไม่งั้น LoadScriptNext
   จะมองว่า libraries เปลี่ยนแล้วเตือน "Performance warning! LoadScript has been reloaded" */
export const MAP_LIBRARIES = ["geometry", "places"];
export const MAP_CENTER_DEFAULT = { lat: 13.7563, lng: 100.5018 }; // กรุงเทพฯ
export const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
export const MAP_OPTIONS = {
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: "greedy",
};

/* ------------------------------------------------------- จับคู่ชื่อทำเลไทย
 * ชื่อจาก Google Geocoder มาพร้อมคำนำหน้า (แขวง/เขต/จังหวัด) และ "กรุงเทพมหานคร"
 * แต่ th_province.json เก็บเป็นชื่อล้วนและใช้ "กรุงเทพฯ" จึงต้องปรับให้เทียบกันได้
 * ------------------------------------------------------------------------ */
export const cleanName = (name) =>
  String(name || "")
    .replace(/^(จังหวัด|อำเภอ|เขต|ตำบล|แขวง)\s*/, "")
    .replace(/^(Amphoe|Khet|Tambon|Khwaeng|Changwat)\s+/i, "")
    .replace(/\s*(Province|District|Subdistrict)$/i, "")
    .replace("กรุงเทพมหานคร", "กรุงเทพฯ")
    .trim();

export const findProvince = (name) => {
  const key = cleanName(name);
  return PROVINCE.find((e) => e.th === key || e.en?.toLowerCase() === key.toLowerCase());
};

export const findDistrict = (province, name) => {
  const key = cleanName(name);
  return province?.district?.find((e) => e.th === key || e.en?.toLowerCase() === key.toLowerCase());
};

export const findSubdistrict = (district, name) => {
  const key = cleanName(name);
  return district?.sub_district?.find((e) => e.th === key || e.en?.toLowerCase() === key.toLowerCase());
};

/** ตัวเลือกของ Select ทั้งสามระดับ — คำนวณจากค่าที่เลือกอยู่ในฟอร์ม */
export const zoneOptions = (provinceName, districtName) => {
  const province = PROVINCE.find((e) => e.th === provinceName);
  const district = province?.district?.find((e) => e.th === districtName);

  return {
    province,
    district,
    provinceOptions: PROVINCE.map((e) => ({ value: e.th, label: e.th })),
    districtOptions: (province?.district || []).map((e) => ({ value: e.th, label: e.th })),
    subdistrictOptions: (district?.sub_district || []).map((e) => ({ value: e.th, label: e.th, code: e.code })),
  };
};

/* ==========================================================================
 * ค้นหาสถานที่ (Places) + แปลงพิกัดเป็นที่อยู่ (Geocoding)
 *
 * ใช้ Places API (New) ผ่าน AutocompleteSuggestion — ตัวเดิม
 * google.maps.places.Autocomplete Google เลิกเปิดให้โปรเจกต์ใหม่ใช้แล้ว
 * (ยิงตรงไปที่ legacy endpoint จะได้ "You're calling a legacy API")
 *
 * ทั้งสองบริการต้องเปิดใช้งานใน Google Cloud Console แยกจาก Maps JavaScript API :
 *   - Places API (New)  → ช่องค้นหา + รายการแนะนำ
 *   - Geocoding API     → คลิก/ลากหมุดบนแผนที่แล้วเติมที่อยู่ (Places ทำ reverse geocode ไม่ได้)
 * ถ้ายังไม่เปิด ทุก request จะถูกปฏิเสธด้วย REQUEST_DENIED / PERMISSION_DENIED
 * ซึ่งฟังก์ชันในไฟล์นี้จะแปลงเป็นคีย์ MAP_ERROR.NOT_ENABLED เพื่อฟ้องสาเหตุจริง
 * ========================================================================== */

/** คีย์ข้อความ error (ตรงกับ th.json / en.json) — ฝั่งหน้าเว็บเอาไปเข้า t() ต่อ */
export const MAP_ERROR = {
  NOT_READY: "map_not_ready",
  NOT_ENABLED: "map_api_not_enabled",
  QUOTA: "map_quota_exceeded",
  NOT_FOUND: "place_not_found",
  FAILED: "place_search_failed",
};

/* Places API (New) โยน Error ส่วน Geocoder คืน status string — รวมมาแปลที่เดียว
   จับด้วยการค้นคำในข้อความ เพราะรูปแบบ error ของสองบริการไม่เหมือนกัน */
const errorKey = (reason) => {
  const text = String(reason || "").toUpperCase();
  if (text.includes("ZERO_RESULTS") || text.includes("NOT_FOUND")) return MAP_ERROR.NOT_FOUND;
  if (text.includes("OVER_QUERY_LIMIT") || text.includes("RESOURCE_EXHAUSTED")) return MAP_ERROR.QUOTA;
  if (
    text.includes("REQUEST_DENIED") ||
    text.includes("PERMISSION_DENIED") ||
    text.includes("NOT ACTIVATED") ||
    text.includes("NOT BEEN USED") ||
    text.includes("DISABLED") ||
    text.includes("API KEY")
  )
    return MAP_ERROR.NOT_ENABLED;
  return MAP_ERROR.FAILED;
};

/* ไลบรารี places — ปกติมากับ script tag แล้ว (MAP_LIBRARIES) แต่เผื่อบาง build
   ที่ยังไม่ผูกมาให้ ก็ดึงเพิ่มผ่าน importLibrary ซึ่ง Maps JS รุ่นใหม่มีให้ */
const placesLibrary = async () => {
  const maps = window.google?.maps;
  if (!maps) return null;
  if (maps.places?.AutocompleteSuggestion) return maps.places;
  if (!maps.importLibrary) return null;
  try {
    return await maps.importLibrary("places");
  } catch {
    return null;
  }
};

/* Session token — Places คิดเงินแบบ "หนึ่ง session" = พิมพ์หลายครั้ง + ดูรายละเอียด 1 ครั้ง
   ถ้าไม่ส่ง token ไป ทุกตัวอักษรที่พิมพ์จะถูกคิดเป็นคำขอแยกกัน */
let session = null;
const sessionToken = (places) => {
  if (!session) session = new places.AutocompleteSessionToken();
  return session;
};

/* ---------------------------------------------- แกะทำเลจาก address components
 * Google ไม่ได้ใช้ type ชุดเดียวกันทั่วประเทศ — ข้อมูลจริงที่ได้จาก Places API (New) :
 *
 *   กรุงเทพฯ    เขต  = sublocality_level_1   แขวง = sublocality_level_2
 *               (ไม่มี administrative_area_level_2 มาด้วยเลย)
 *   ต่างจังหวัด  อำเภอ = administrative_area_level_2
 *               ตำบล = sublocality_level_1 หรือ locality (แล้วแต่สถานที่)
 *
 * จะเห็นว่า sublocality_level_1 แปลว่า "เขต" ในกรุงเทพฯ แต่แปลว่า "ตำบล" ในต่างจังหวัด
 * ดูจาก type อย่างเดียวจึงหยิบ "เขตปทุมวัน" ไปเป็นแขวงแล้วจับคู่ไม่ติดทั้งคู่ (ช่องว่างทั้งแถว)
 * ตัวช่วยข้างล่างจึงไล่ผู้เข้าชิงจากสัญญาณที่แน่นอนที่สุดลงมา แล้วเอาชื่อไปจับคู่กับ
 * th_province.json จริง ๆ ตัวไหนจับคู่ติดก่อนถือว่าใช่ — ชื่อที่ Google ให้มาไม่ครบหรือ
 * เป็นภาษาอังกฤษบางตัว (เช่น "Muang") ก็ยังกู้คืนได้จากชั้นถัดไป
 * -------------------------------------------------------------------------- */

/** คำนำหน้าไทย/อังกฤษ — สัญญาณที่แม่นกว่า type เพราะติดมากับชื่อโดยตรง */
const DISTRICT_PREFIX = /^(เขต|อำเภอ|อ\.)\s*|^(Khet|Amphoe|Amphur)\s+/i;
const SUBDISTRICT_PREFIX = /^(แขวง|ตำบล|ต\.)\s*|^(Khwaeng|Kwaeng|Tambon)\s+/i;
/* route เก็บทั้งถนนและซอยปนกัน ("ถนน พระรามที่ 1" กับ "ซอย ทองหล่อ 10")
   ช่องในฟอร์มเป็น "ซอย" จึงต้องคัดถนนสายหลักออก */
const ROAD_PREFIX = /^(ถนน|ถ\.)\s*|^(Thanon|Road|Rd\.?|Highway)\s+/i;
const SOI_PREFIX = /^(ซอย|ซ\.)\s*|^Soi\s+/i;

export const zoneFromComponents = (components) => {
  /* รองรับทั้ง Geocoder (long_name / short_name) และ Places API (New) (longText / shortText)
     บาง component ไม่มี types ติดมาเลย (เจอจริงกับที่อยู่ที่ Google ข้อมูลไม่ครบ) จึงต้องกัน undefined */
  const list = (components || [])
    .map((e) => ({
      text: String(e.long_name || e.longText || e.short_name || e.shortText || "").trim(),
      types: e.types || [],
    }))
    .filter((e) => e.text);

  const pick = (type) => list.find((e) => e.types.includes(type))?.text || "";

  const province = findProvince(pick("administrative_area_level_1"));
  if (!province) return null;

  /* administrative_area_level_2 เป็นตัวชี้ขาดว่าอยู่รูปแบบไหน :
     มี = ต่างจังหวัด (ตำบลอยู่ชั้น sublocality_level_1) / ไม่มี = กรุงเทพฯ (เขตอยู่ชั้นนั้นแทน) */
  const upcountry = list.some((e) => e.types.includes("administrative_area_level_2"));

  /* เรียงผู้เข้าชิงจาก "มั่นใจที่สุด" ไป "เดา" : คำนำหน้า → type ตามรูปแบบพื้นที่ → ที่เหลือทั้งหมด
     ตัวซ้ำไม่เป็นไร เพราะเราหยุดที่ตัวแรกที่จับคู่กับ th_province.json ได้ */
  const ranked = (prefix, types) =>
    [...list.filter((e) => prefix.test(e.text)), ...types.flatMap((type) => list.filter((e) => e.types.includes(type))), ...list].map(
      (e) => e.text
    );

  const districtNames = ranked(
    DISTRICT_PREFIX,
    upcountry ? ["administrative_area_level_2"] : ["sublocality_level_1", "locality"]
  );
  const subdistrictNames = ranked(
    SUBDISTRICT_PREFIX,
    upcountry ? ["sublocality_level_1", "locality", "administrative_area_level_3"] : ["sublocality_level_2"]
  );

  let district = districtNames.map((name) => findDistrict(province, name)).find(Boolean) || null;
  let subdistrict = district ? subdistrictNames.map((name) => findSubdistrict(district, name)).find(Boolean) || null : null;

  /* ได้แขวง/ตำบลแต่จับคู่เขต/อำเภอไม่ติด (เช่น Google ส่งชื่ออำเภอมาเป็นภาษาอังกฤษแบบย่อ)
     → เดาเขตย้อนจากชื่อแขวง ใช้ได้เฉพาะตอนที่ชื่อนั้นมีอยู่เขตเดียวในจังหวัด ไม่งั้นเดาผิด */
  if (!district)
    for (const name of subdistrictNames) {
      const owners = (province.district || []).filter((e) => findSubdistrict(e, name));
      if (owners.length === 1) {
        district = owners[0];
        subdistrict = findSubdistrict(district, name);
        break;
      }
    }

  const route = list.find((e) => e.types.includes("route"))?.text || "";
  const soi = ROAD_PREFIX.test(route) ? "" : route.replace(SOI_PREFIX, "").trim();

  return {
    province: province.th,
    district: district?.th || "",
    subdistrict: subdistrict?.th || "",
    postcode: pick("postal_code") || (subdistrict?.code ? String(subdistrict.code) : ""),
    /* ใส่คีย์ soi เฉพาะตอนหาเจอ — ผู้เรียก spread ทับฟอร์ม การส่ง "" ไปจะไปล้างซอยที่ผู้ใช้
       พิมพ์เองทิ้งทุกครั้งที่ขยับหมุด ทั้งที่สถานที่ส่วนใหญ่ Google ไม่ได้บอกซอยมาด้วยซ้ำ */
    ...(soi ? { soi } : {}),
  };
};

/**
 * รายการสถานที่แนะนำจากคำที่พิมพ์ (จำกัดเฉพาะในไทย)
 * @param {string} keyword
 * @returns {Promise<{list:Array<{id:string,main:string,secondary:string,prediction:object}>, error?:string}>}
 */
export const searchPlaces = async (keyword) => {
  const input = String(keyword || "").trim();
  if (!input) return { list: [] };

  const places = await placesLibrary();
  if (!places?.AutocompleteSuggestion) return { list: [], error: MAP_ERROR.NOT_READY };

  try {
    const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      /* ขอเป็นภาษาไทยเสมอ ไม่ตามภาษาหน้าจอ เพราะชื่อที่ได้กลับมาต้องเอาไปจับคู่กับ
         th_province.json ที่เก็บชื่อไทย และ Select จังหวัด/เขต/แขวง ก็เป็นภาษาไทยล้วน */
      language: "th",
      region: "th",
      includedRegionCodes: ["th"],
      sessionToken: sessionToken(places),
    });

    const list = (suggestions || [])
      .map((item) => item.placePrediction)
      .filter(Boolean)
      .map((prediction) => ({
        id: prediction.placeId,
        main: prediction.mainText?.text || prediction.text?.text || "",
        secondary: prediction.secondaryText?.text || "",
        prediction,
      }));

    return { list, error: list.length ? undefined : MAP_ERROR.NOT_FOUND };
  } catch (error) {
    return { list: [], error: errorKey(error?.message) };
  }
};

/**
 * รายละเอียดของสถานที่ที่ผู้ใช้เลือกจากรายการแนะนำ → พิกัด + ทำเล
 * @param {object} prediction ตัว prediction ที่ติดมากับรายการจาก searchPlaces
 * @returns {Promise<{lat?:number,lng?:number,zone?:object|null,name?:string,error?:string}>}
 */
export const placeDetail = async (prediction) => {
  try {
    const place = prediction.toPlace();
    await place.fetchFields({ fields: ["location", "addressComponents", "displayName"] });
    session = null; // จบ session แล้ว รอบหน้าเริ่มนับใหม่

    if (!place.location) return { error: MAP_ERROR.NOT_FOUND };

    return {
      lat: place.location.lat(),
      lng: place.location.lng(),
      zone: zoneFromComponents(place.addressComponents),
      name: place.displayName || "",
    };
  } catch (error) {
    session = null;
    return { error: errorKey(error?.message) };
  }
};

/**
 * พิกัด → ทำเล (ใช้ตอนคลิกหรือลากหมุดบนแผนที่)
 * @param {{lat:number,lng:number}} location
 * @returns {Promise<{zone?:object|null, error?:string}>}
 */
export const reverseGeocode = (location) =>
  new Promise((resolve) => {
    if (!window.google?.maps?.Geocoder) return resolve({ error: MAP_ERROR.NOT_READY });

    new window.google.maps.Geocoder().geocode({ location, language: "th" }, (results, status) => {
      if (status !== "OK" || !results?.length) return resolve({ error: errorKey(status) });
      resolve({ zone: zoneFromComponents(results[0].address_components) });
    });
  });

/* ------------------------------------------------- จุดสนใจใกล้เคียง (สถานี)
 * สถานีรถไฟฟ้าเก็บเป็นไฟล์ในเครื่อง (th_transit_station.json) แทนการค้นผ่าน Places API
 * เพราะชุดสถานีเป็นข้อมูลปิดที่แทบไม่เปลี่ยน และยังทำงานได้แม้ไม่มี map key
 * ------------------------------------------------------------------------ */
export const STATION_RADIUS_KM = 2;
export const STATION_MAX = 3;

/* ระยะเส้นตรงระหว่างสองพิกัด หน่วยกิโลเมตร (haversine)
   ไม่ใช้ library geometry ของ Google เพราะส่วนนี้ต้องใช้ได้แม้แผนที่ยังไม่โหลด */
export const distanceKm = (from, to) => {
  const rad = (deg) => (deg * Math.PI) / 180;
  const dLat = rad(to.lat - from.lat);
  const dLng = rad(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(from.lat)) * Math.cos(rad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(a));
};

const isTh = () => (localStorage.getItem("lang") || "TH") !== "EN";

/* ชื่อที่เอาไปแสดงบนประกาศ เช่น "MRT ลาดพร้าว" / "BTS อโศก" */
export const stationLabel = (station) => `${station.system} ${(isTh() ? station.name_th : station.name_en) || station.name_th}`;

/**
 * สถานีที่อยู่ใกล้หมุดที่สุด (ตัดชื่อที่มีอยู่แล้วออก)
 * @param {{lat:number,lng:number}} pin
 * @param {Array<{label:string}>} current รายการจุดสนใจที่กรอกไว้แล้ว
 * @returns {Array<{type:string,label:string,distance:string}>} รายการที่พร้อมต่อท้าย
 */
export const nearestStations = (pin, current = []) =>
  TRANSIT_STATION.map((station) => ({ station, distance: distanceKm(pin, station) }))
    .filter((e) => e.distance <= STATION_RADIUS_KM)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, STATION_MAX)
    .map(({ station, distance }) => ({ type: "transit", label: stationLabel(station), distance: distance.toFixed(2) }))
    .filter((item) => !current.some((e) => String(e.label || "").trim() === item.label));

/** พิกัดที่ใช้ได้จริง — ช่อง input เป็นสตริง จึงต้องกรองค่าที่ยังพิมพ์ไม่จบทิ้ง */
export const coord = (v) => {
  const n = Number(v);
  return v !== "" && v !== null && v !== undefined && !Number.isNaN(n) ? n : null;
};

/** ที่อยู่เต็ม — สูตรเดียวกับ buildAddress ฝั่ง backend */
export const fullAddress = (d) =>
  [d.soi, d.subdistrict, d.district, d.province, d.postcode].filter((part) => !!String(part || "").trim()).join(" ");
